<template>
  <b-card>
    <!-- form -->
    <validation-observer ref="hardwareUpdate">
      <b-form>
        <b-row>
          <b-col md="12">
            <div class="section-block mb-4">
              <div class="section-block__title">
                <feather-icon
                  icon="ListIcon"
                  width="24"
                  height="24"
                />
                <h2>Hardware specifications</h2>
              </div>
              <b-form-group
                label="Name*"
                label-class="label-class"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Name"
                  vid="name"
                  rules="required|max:255"
                >
                  <b-form-input
                    v-model="form.name"
                    autocomplete="off"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Model*"
                label-class="label-class"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Model"
                  vid="model_id"
                  rules="required"
                >
                  <v-select
                    v-model="form.model_id"
                    :options="modelList"
                    :reduce="model => model.id"
                    label="name"
                    class="selector-height"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-row
                class="neighbouring-form-container"
              >
                <b-col
                  class="neighbouring-form-group"
                >
                  <b-form-group
                    label="Serial number*"
                    label-class="label-class"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Serial number"
                      vid="serial_number"
                      rules="required|alpha-num|min:11|max:14"
                    >
                      <b-form-row>
                        <b-col>
                          <b-form-input
                            v-model="form.serial_number"
                            :state="errors.length > 0 ? false:null"
                            autocomplete="off"
                            placeholder="Serial number"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-col>
                      </b-form-row>

                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-form-group
                  label="Warranty expiry at"
                  label-class="label-class"
                  class="neighbouring-form-group"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Warranty expiry at"
                    vid="warranty_expiry_at"
                  >
                    <b-form-datepicker
                      id="warranty_expiry_at"
                      v-model="form.warranty_expiry_at"
                      :min="minDate"
                      locale="en-GB"
                      class="mb-1"
                      :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-form-row>

              <b-form-group
                label="Comment"
                label-class="label-class"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Comment"
                  vid="comment"
                  rules="max:255"
                >
                  <b-form-input
                    id="Comment"
                    v-model="form.comment"
                    autocomplete="off"
                    placeholder="Comment"
                    class="mb-1"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </div>
            <div
              v-if="!isClient"
              class="section-block mb-4"
            >
              <div class="section-block__title">
                <feather-icon
                  icon="UsersIcon"
                  width="24"
                  height="24"
                />
                <h2>Customer Details</h2>
              </div>
              <b-form-group
                v-if="!company"
                label="Company"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Company"
                  vid="company_id"
                >
                  <v-select
                    v-model="company"
                    v-b-tooltip.hover.left
                    :options="companies"
                    class="owner-select"
                    placeholder="Enter company name"
                    label="name"
                    @search="onSearch($event, 'company')"
                    @input="onSelectCompany($event)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <div
                v-else
                class="mb-2"
              >
                <b-button
                  variant="primary"
                  class="transparent-button"
                  @click="onReselectCompany"
                >
                  Clear selection
                </b-button>
              </div>
              <div v-if="company || site">
                <b-form-group
                  v-if="$can('view', SELECT_MASTER_DISTRIBUTOR) && masterDistributor"
                  label="Master Distributor"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Master Distributor"
                    vid="master_distributor_id"
                  >
                    <v-select
                      v-model="masterDistributor"
                      v-b-tooltip.hover.left
                      :disabled="site"
                      :options="masterDistributors"
                      :class="selected[0] !== 'Master Distributor' ? 'disabled-clear owner-select' : 'owner-select'"
                      placeholder="Enter name"
                      label="name"
                      :title="site ? 'This field can\'t be edited as it\'s linked to the selected site.' : ''"
                      @input="onSelectedUser($event, 'master_distributor')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <b-form-group
                  v-if="$can('view', SELECT_DISTRIBUTOR) && (distributor || masterDistributor)"
                  label="Distributor"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Distributor"
                    vid="distributor_id"
                  >
                    <v-select
                      v-model="distributor"
                      v-b-tooltip.hover.left
                      :options="distributors"
                      placeholder="Enter name"
                      :disabled="site"
                      :class="selected[0] !== 'Distributor' ? 'disabled-clear owner-select' : 'owner-select'"
                      label="name"
                      :title="site ? 'This field can\'t be edited as it\'s linked to the selected site.' : ''"
                      @search="onSearch($event, 'distributor')"
                      @input="onSelectedUser($event, 'distributor')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <b-form-group
                  v-if="$can('view', SELECT_RESELLER) && (reseller || distributor)"
                  label="Reseller"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Reseller"
                    vid="reseller_id"
                  >
                    <v-select
                      v-model="reseller"
                      v-b-tooltip.hover.left
                      :options="resellers"
                      placeholder="Enter name"
                      :disabled="site"
                      label="name"
                      :class="selected[0] !== 'Reseller' ? 'disabled-clear owner-select' : 'owner-select'"
                      :title="site ? 'This field can\'t be edited as it\'s linked to the selected site.' : ''"
                      @search="onSearch($event, 'reseller')"
                      @input="onSelectedUser($event, 'reseller')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <b-form-group
                  v-if="$can('view', SELECT_CLIENT) && (client || reseller)"
                  label="Client"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Client"
                    vid="client_id"
                  >
                    <v-select
                      v-model="client"
                      v-b-tooltip.hover.left
                      :options="clients"
                      placeholder="Enter name"
                      :disabled="site"
                      label="name"
                      :class="selected[0] !== 'Client' ? 'disabled-clear owner-select' : 'owner-select'"
                      :title="site ? 'This field can\'t be edited as it\'s linked to the selected site.' : ''"
                      @search="onSearch($event, 'client')"
                      @input="onSelectedUser($event, 'client')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </div>
            </div>
            <div class="section-block mb-4">
              <div class="section-block__title">
                <feather-icon
                  icon="MapIcon"
                  width="24"
                  height="24"
                />
                <h2>Site</h2>
              </div>
              <p
                class="text-muted mt-0 mb-2"
              >
                Selecting a site will automatically prefill the Customer Details and lock them from manual changes.
              </p>
              <b-form-group
                label=""
              >
                <validation-provider
                  #default="{ errors }"
                  name="Site"
                  vid="site_id"
                >
                  <v-select
                    v-model="site"
                    :options="siteList"
                    placeholder="Enter name"
                    class="selector-height"
                    label="name"
                    @search="onSearch($event, 'site')"
                    @input="onSelectedUser($event, 'site')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </div>
            <div
              v-if="item"
              class="section-block  mb-4"
            >
              <div class="section-block__title">
                <feather-icon
                  icon="InfoIcon"
                  width="24"
                  height="24"
                />
                <h2>Additional details</h2>
              </div>
              <p class="status-label">
                Status:
              </p>
              <div class="d-flex justify-content-between align-items-center mb-1 license-status-container">
                <b-badge
                  v-if="!item.readOnly.is_online"
                  pill
                  variant="danger"
                  class="license-badge"
                >Offline</b-badge>
                <b-badge
                  v-else
                  pill
                  variant="success"
                  class="license-badge"
                >Online</b-badge>
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  type="submit"
                  class="d-flex align-items-center refresh-button"
                  variant="primary"
                  :disabled="isHardwareReloading"
                  @click.prevent="handleReloadItem(item.id)"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    width="16"
                    height="16"
                  />
                  Reload
                  <b-spinner
                    v-if="isHardwareReloading"
                    class="ml-1"
                    style="width:14px; height:14px"
                    label="Spinning"
                  />
                </b-button>
              </div>
              <b-form-group
                label="Current Firmware"
                label-class="label-class"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Current Firmware"
                  vid="current_system_version"
                >
                  <b-form-input
                    v-model="item.readOnly.current_system_version"
                    disabled
                    placeholder="Current Firmware"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Original System Information"
                label-class="label-class"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Original System Information"
                  vid="original_router_board_version"
                >
                  <b-form-input
                    v-model="item.readOnly.original_router_board_version"
                    disabled
                    placeholder="Original System Information"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Last Polled datetime"
                label-class="label-class"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Last Polled datetime"
                  vid="polled_at"
                >
                  <b-form-input
                    v-model="item.readOnly.polled_at"
                    disabled
                    placeholder="Last Polled datetime"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </div>
          </b-col>
          <!-- submit button -->
          <b-col
            cols="12"
            class="text-right"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              class="w-100"
              type="submit"
              variant="primary"
              @click.prevent="validationForm"
            >
              <div
                v-if="isFormSubmitting"
              >
                <b-spinner
                  label="Spinning"
                  small
                />
              </div>
              {{ isFormSubmitting ? '' : 'Submit' }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-card>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormInput, BFormGroup, BForm, BRow, BCol, BButton, BFormDatepicker, BCard, BFormRow, BBadge, BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import {
  required, max, alphaNum, numeric, min,
} from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import rolesPermission from '@/libs/acl_roles/rolesPermission'
import { mapActions, mapMutations, mapState } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import { debounce } from '@/utils/utils'

export default {
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    ValidationProvider,
    ValidationObserver,
    BFormInput,
    BFormGroup,
    BForm,
    BFormRow,
    BRow,
    BCol,
    BButton,
    BFormDatepicker,
    BBadge,
    BSpinner,
    vSelect,
  },
  data() {
    return {
      form: {
        id: null,
        name: '',
        model_id: '',
        serial_number: '',
        warranty_expiry_at: null,
        comment: null,
      },
      timeoutUpdate: null,
      isFormSubmitting: false,
      isHardwareReloading: false,
      minDate: '',
      options: ['Client', 'Reseller', 'Distributor', 'Master Distributor'],
      modelList: [],
      required,
      min,
      max,
      alphaNum,
      numeric,
      selected: [],
      SELECT_MASTER_DISTRIBUTOR: rolesPermission.SELECT_MASTER_DISTRIBUTOR,
      SELECT_DISTRIBUTOR: rolesPermission.SELECT_DISTRIBUTOR,
      SELECT_RESELLER: rolesPermission.SELECT_RESELLER,
      SELECT_CLIENT: rolesPermission.SELECT_CLIENT,
      SELECT_SITE: rolesPermission.SELECT_SITE,
      HARDWARE_SELECT_ANY_WARRANTY_EXPIRY_AT: rolesPermission.HARDWARE_SELECT_ANY_WARRANTY_EXPIRY_AT,
      originSite: null,
      company: null,
    }
  },
  computed: {
    ...mapFields('user', [
      'masterDistributor',
      'distributor',
      'reseller',
      'client',
      'site',
      'masterDistributorList',
      'clients',
      'resellers',
      'distributors',
      'masterDistributors',
      'distributorList',
      'resellerList',
      'clientList',
      'siteList',
    ]),
    ...mapState('companyManagement', {
      companies: 'items',
    }),
    ...mapState('hardware', [
      'item',
    ]),
    isClient() {
      const { roles } = JSON.parse(localStorage.getItem('userData'))
      return roles.includes('client')
    },
  },
  watch: {
    distributor(val) {
      clearTimeout(this.timeoutUpdate)
      this.timeoutUpdate = setTimeout(() => {
        this.handleSiteFilter(val)
      }, 500)
      if (!val) {
        this.selected = ['Master Distributor']
        this.client = null
        this.reseller = null
      } else if (!this.reseller && !this.client) {
        this.selected = ['Distributor']
      }
    },
    reseller(val) {
      clearTimeout(this.timeoutUpdate)
      this.timeoutUpdate = setTimeout(() => {
        this.handleSiteFilter(val)
      }, 500)
      if (!val) {
        this.selected = ['Distributor']
        this.client = null
      } else if (!this.client) {
        this.selected = ['Reseller']
      }
    },
    masterDistributor(val) {
      clearTimeout(this.timeoutUpdate)
      this.timeoutUpdate = setTimeout(() => {
        this.handleSiteFilter(val)
      }, 500)
      if (!val) {
        this.selected = ['Master Distributor']
        this.distributor = null
        this.reseller = null
        this.client = null
      } else if (!this.reseller && !this.distributor && !this.client) {
        this.selected = ['Master Distributor']
      }
    },
    client(val) {
      clearTimeout(this.timeoutUpdate)
      this.timeoutUpdate = setTimeout(() => {
        this.handleSiteFilter(val)
      }, 500)
      if (!val) {
        this.selected = ['Reseller']
      } else {
        this.selected = ['Client']
      }
    },
    site() {
      if (this.site && this.siteList && !this.siteList.find(item => item.id === this.site.id)) {
        this.site = null
      }
    },
  },
  async created() {
    // Fetch all company lists in parallel for better performance
    await Promise.all([
      this.fetchCompaniesList({ query: '', company_type: 'client' }),
      this.fetchCompaniesList({ query: '', company_type: 'reseller' }),
      this.fetchCompaniesList({ query: '', company_type: 'distributor' }),
      this.fetchCompaniesList({ query: '', company_type: 'master-distributor' }),
    ])

    console.log('users', {
      masterDistributor: this.masterDistributor,
      distributor: this.distributor,
      reseller: this.reseller,
      client: this.client,
      site: this.site,
    })
    // Set company based on priority
    setTimeout(() => {
      this.company = this.client
        || this.reseller
        || this.distributor
        || this.masterDistributor
        || null
    }, 300)
    if (this.$can('view', this.SELECT_MASTER_DISTRIBUTOR)) {
      this.options = ['Client', 'Reseller', 'Distributor', 'Master Distributor']
    } else if (this.$can('view', this.SELECT_DISTRIBUTOR)) {
      this.options = ['Client', 'Reseller', 'Distributor']
    } else if (this.$can('view', this.SELECT_RESELLER)) {
      this.options = ['Client', 'Reseller']
    } else if (this.$can('view', this.SELECT_CLIENT)) {
      this.options = ['Client']
    } else {
      this.options = []
    }
    await this.fetchCompanies({ query: '' })
    await this.search({ query: '', company_type: 'site' }, this.fetchSiteList)
    await this.fetchModelList()
  },
  mounted() {
    const { id } = this.$route.params
    if (id) {
      this.fetchModel(id)
    } else {
      this.$router.push({ name: 'error-404' })
    }
    if (!this.$can('view', this.HARDWARE_SELECT_ANY_WARRANTY_EXPIRY_AT)) {
      this.minDate = new Date()
    }
  },
  methods: {
    ...mapActions('user', [
      'fetchCompaniesList',
      'fetchHardwareSelectedCompany',
    ]),
    ...mapActions('hardware', [
      'getItem',
      'updateItem',
      'reloadItem',
    ]),
    ...mapActions('companyManagement', {
      fetchCompanies: 'getItems',
    }),
    ...mapMutations({
      selectCompanies: 'user/SELECT_COMPANIES',
      resetUserState: 'user/RESET_STATE',
    }),
    handleSiteFilter() {
      let id = null
      if (this.client) {
        id = this.client.id
      } else if (this.reseller) {
        id = this.reseller.id
      } else if (this.distributor) {
        id = this.distributor.id
      } else if (this.masterDistributor) {
        id = this.masterDistributor.id
      }
      this.fetchSiteList({
        parent_company_id: id,
      })
    },
    onReselectCompany() {
      this.resetUserState()
      this.company = null
      this.masterDistributor = null
      this.distributor = null
      this.reseller = null
      this.client = null
      this.site = null
      this.selected = []
    },
    onSelect(option) {
      if (option === 'Client') {
        this.selected = ['Client']
      }
      if (option === 'Reseller') {
        this.selected = ['Reseller']
        this.client = null
      }
      if (option === 'Distributor') {
        this.selected = ['Distributor']
        this.reseller = null
        this.client = null
      }
      if (option === 'Master Distributor') {
        this.selected = ['Master Distributor']
        this.distributor = null
        this.reseller = null
        this.client = null
      }
    },
    onSearch(query, type) {
      if (type === 'company') {
        if (!query) {
          this.fetchCompanies({ search: '', searchFields: '', searchJoin: '' })
          return
        }
        this.fetchCompanies({
          search: `name:${query}`,
          searchFields: 'name',
          searchJoin: 'and',
        })
      }
      if (!query) {
        return
      }
      let payload = {
        query,
        company_type: type,
      }
      if (type === 'master-distributor') {
        this.reseller = null
        this.client = null
        this.distributor = null
        this.search(payload, this.fetchCompaniesList)
      }
      if (type === 'distributor') {
        this.reseller = null
        this.client = null
        this.search(payload, this.fetchCompaniesList)
      }
      if (type === 'reseller') {
        this.client = null
        if (this.distributor) {
          payload = {
            ...payload,
            ...{ distributor_id: this.distributor.id },
          }
        }
        this.search(payload, this.fetchCompaniesList)
      }
      if (type === 'client') {
        if (this.distributor) {
          payload = {
            ...payload,
            ...{ distributor_id: this.distributor.id },
          }
        }
        if (this.reseller) {
          payload = {
            ...payload,
            ...{ reseller_id: this.reseller.id },
          }
        }
        this.search(payload, this.fetchCompaniesList)
      }
      if (type === 'site') {
        if (this.distributor) {
          payload = {
            ...payload,
            ...{ distributor_id: this.distributor.id },
          }
        }
        if (this.reseller) {
          payload = {
            ...payload,
            ...{ reseller_id: this.reseller.id },
          }
        }
        if (this.client) {
          payload = {
            ...payload,
            ...{ client_id: this.client.id },
          }
        }
        this.search(payload, this.fetchSiteList)
      }
    },
    search: debounce((payload, fetchAction) => {
      fetchAction({ ...payload })
    }, 350),
    async onSelectCompany(option) {
      const selectedUser = {
        id: option.id,
        name: option.name,
      }
      this.fetchHardwareSelectedCompany({
        company_id: option.id,
      })
      switch (option.type.name) {
        case 'Master Distributor':
          this.masterDistributor = selectedUser
          this.distributor = null
          this.reseller = null
          this.client = null
          this.site = null
          break
        case 'Distributor':
          this.distributor = selectedUser
          this.reseller = null
          this.client = null
          this.site = null
          break
        case 'Reseller':
          this.reseller = selectedUser
          this.client = null
          this.site = null
          break
        case 'Client':
          this.client = selectedUser
          this.site = null
          break
        default:
          this.masterDistributor = null
          this.distributor = null
          this.reseller = null
          this.client = null
          this.site = null
          break
      }
    },
    async onSelectedUser(option, type) {
      let payload = {}
      payload.company_id = option.id
      if (type === 'master-distributor') {
        this.distributor = null
        this.reseller = null
        this.client = null
        this.distributorList = []
        this.resellerList = []
        this.clientList = []
        this.fetchHardwareSelectedCompany(payload)
      }
      if (type === 'distributor') {
        this.reseller = null
        this.client = null
        this.resellerList = []
        this.clientList = []
        this.selected = ['Distributor']
        if (!option) {
          return
        }
        this.fetchHardwareSelectedCompany(payload)
      }
      if (type === 'reseller') {
        this.client = null
        this.selected = ['Reseller']
        this.clientList = []
        if (!option) {
          return
        }
        this.fetchHardwareSelectedCompany(payload)
      }
      if (type === 'client') {
        if (!option) {
          return
        }
        this.selected = ['Client']
        if (!this.reseller && this.distributor) {
          payload = {
            ...payload,
            ...{ distributor_id: this.distributor.id },
          }
        }
        payload = {
          ...payload,
        }
        this.fetchHardwareSelectedCompany(payload)
      }
      if (type === 'site') {
        if (!option) {
          return
        }
        payload = {
          ...payload,
          ...{ site_id: option.id },
        }
        delete payload.company_id
        this.fetchHardwareSelectedCompany(payload)
      }
    },
    async fetchModelList() {
      try {
        const { data } = await this.$http.get('/hardware/model/list')
        this.modelList = data.data
      } catch (error) {
        console.log('error :>> ', error)
      }
    },
    async fetchSiteList({ ...payload }) {
      try {
        const { data } = await this.$http.get('/site/list', { params: { ...payload } })
        this.siteList = data.data
        if (this.site) {
          const site = this.siteList.find(item => item.id === this.site.id)
          if (!site) {
            this.site = null
          }
        }
      } catch (error) {
        console.log('error :>> ', error)
      }
    },
    async validationForm() {
      const valid = await this.$refs.hardwareUpdate.validate()
      if (valid) {
        try {
          let payload = {
            ...this.form,
          }
          if (this.$can('view', this.SELECT_MASTER_DISTRIBUTOR)) {
            payload = {
              ...payload,
              ...{ master_distributor_id: this.masterDistributor ? this.masterDistributor.id : null },
            }
          }
          if (this.$can('view', this.SELECT_DISTRIBUTOR)) {
            payload = {
              ...payload,
              ...{ distributor_id: this.distributor ? this.distributor.id : null },
            }
          }
          if (this.$can('view', this.SELECT_RESELLER)) {
            payload = {
              ...payload,
              ...{ reseller_id: this.reseller ? this.reseller.id : null },
            }
          }
          if (this.$can('view', this.SELECT_CLIENT)) {
            payload = {
              ...payload,
              ...{ client_id: this.client ? this.client.id : null },
            }
          }
          payload = {
            ...payload,
            ...{ site_id: this.site ? this.site.id : null },
          }
          this.isFormSubmitting = true
          await this.updateItem({
            ...{ id: this.id },
            ...payload,
          })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success!',
              icon: 'BellIcon',
              variant: 'success',
            },
          })
          this.$router.push({ name: 'hardware-list' })
        } catch (error) {
          const { status } = error.response
          const errorMessage = error.response?.data?.message
          if (!status) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Server error',
                icon: 'BellIcon',
                variant: 'danger',
              },
            })
          }
          if (errorMessage) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Cannot create hardware',
                content: errorMessage,
                icon: 'BellIcon',
                variant: 'danger',
              },
            })
          }
          if (status === 422) {
            const { fields } = error.response.data
            this.$refs.hardwareUpdate.setErrors(fields)
          }
        } finally {
          this.isFormSubmitting = false
        }
      }
    },
    async handleReloadItem(id) {
      this.isHardwareReloading = true
      try {
        const { data } = await this.reloadItem(id)
        this.item.readOnly = data
        // eslint-disable-next-line prefer-destructuring
        this.item.readOnly.polled_at = new Date(this.item.readOnly.polled_at).toISOString().replace('T', ' ').replace('Z', '')
          .split('.')[0]
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'BellIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        const { status } = error.response
        if (!status) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Server error',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.response.data.message,
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        }
      } finally {
        this.isHardwareReloading = false
      }
    },
    async fetchModel(id) {
      try {
        const { data } = await this.getItem(id)
        this.form = {
          ...this.item,
        }
        this.selectCompanies(data)
      } catch (error) {
        const { status } = error.response
        if (status === 404) {
          this.$router.push({ name: 'error-404' })
        }
        if (!status) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Server error',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        }
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetUserState()
    next()
  },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

</style>
